import { authApi } from './auth'
import { api } from './client'

export interface ApiUserWithTeams {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  storeIds: string[]
  admin: boolean
  isActive: boolean
  teams: Array<{
    id: number
    name: string
    type: string
  }>
  createdAt: string
  updatedAt: string
}

/** Decode a JWT payload without verifying signature (client-side only). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const usersApi = {
  /**
   * Compose the FE's "me" view from the authoritative GET /auth/profile +
   * the JWT's `isAdmin` claim.
   *
   * Why both? `/auth/profile` is authoritative for everything except
   * `admin` — the User entity declares `admin` as `hidden: true`, so the
   * serializer strips it. The JWT carries `isAdmin` as a claim, so we
   * decode the token just for that one field.
   */
  getMe: async (): Promise<ApiUserWithTeams> => {
    const token = localStorage.getItem('etx_access_token')
    if (!token) throw new Error('No access token')

    const profile = await authApi.getProfile()
    const admin = (decodeJwtPayload(token)?.isAdmin as boolean) ?? false

    return {
      id: profile.id,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      storeIds: profile.teams.map((t) => String(t.id)),
      admin,
      isActive: profile.isActive,
      teams: profile.teams.map((t) => ({ id: t.id, name: t.name, type: t.type })),
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
    }
  },

  /** One-shot fetch used by /welcome/profile to pre-fill the name fields with
   *  whatever the inviter set on the auto-created user. Hits the existing
   *  GET /v1/users/me — no new BE work required. */
  fetchProfile: async () => {
    const me = await api.get<{
      firstName: string | null
      lastName: string | null
      email: string
    }>('/users/me')
    return { firstName: me.firstName, lastName: me.lastName, email: me.email }
  },
}
